import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Box,
  FormControl,
  Select,
  MenuItem,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import Loading from "./Loading";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import FormatDateTime from "../../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import { useTranslation } from "react-i18next";
import ExportToPDF from "./export/ExportToPDF";
import debounce from "lodash.debounce";
import { getAccess } from "../../../../permissions/Permissions";
import EditClient from "./EditClient";

const tableCellStyle = {
  textAlign: "center",
};

const statusColors = {
  14: "#ffebee", // Coming_Soon: light red
  13: "#ffccbc", // No_Answer: light orange
  12: "#e8f5e9", // New: light green
  11: "#fff9c4", // Busy: light yellow
  10: "#f3e5f5", // In_Correct_No: light purple
  9: "#e8eaf6", // Whatsapp: light indigo
  8: "#fce4ec", // Disturb: light pink
  7: "#e1f5fe", // Information: light cyan
  6: "#f1f8e9", // Phone_Off: light lime
  5: "#e0f2f1", // Fixed: light teal
  4: "#ffecb3", // Dont Want: light amber
  3: "#dcedc8", // Done: light green
  2: "#e0f7fa", // Appointment: light cyan
  1: "#d1c4e9", // FollowUp: light purple
};

const AllClients = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.user?.token || user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [statusID, setStatusID] = useState("");
  const [followUpByID, setFollowUpByID] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [total, setTotal] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalClient, setTotalClient] = useState(0);
  const [id, setID] = useState("");

  // Edit dialog state
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const debouncedSetKeyword = useMemo(
    () => debounce((value) => setSearchTerm(value), 500),
    []
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debouncedSetKeyword(e.target.value);
  };

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  const { data: usersData } = useFetchObjects(
    ["users"],
    `users/`, // Removed position parameter that's causing 400 error
    token
  );

  const apiUrl = `all-clients/?search=${searchTerm}&status=${statusID}&id=${id}&follow_up_by=${followUpByID}&created_at=${createdAt}&page=${page}&page_size=${rowsPerPage}`;

  const {
    data: clients,
    isError,
    refetch,
  } = useFetchObjects(["clients"], apiUrl, token);

  const { data: status } = useFetchObjects(["status"], "status", token);

  useEffect(() => {
    setIsLoading(true);
    refetch();
  }, [
    searchTerm,
    statusID,
    followUpByID,
    createdAt,
    id,
    page,
    rowsPerPage,
    refetch,
  ]);

  useEffect(() => {
    if (clients && typeof clients.count !== "undefined") {
      setTotalClient(clients.count);
    }
    setIsLoading(false);
  }, [clients]);

  useEffect(() => {
    setTotal(clients?.results?.length);
  }, [clients]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const exportColumns = [
    { label: t("call_center.id"), id: "id" },
    { label: t("call_center.name"), id: "full_name" },
    { label: t("call_center.contact"), id: "contact" },
    { label: t("call_center.procedure"), id: "procedure_name" },
    { label: t("call_center.procedureDate"), id: "procedure_date" },
    { label: t("call_center.status"), id: "status" },
    { label: t("call_center.price"), id: "price" },
    { label: t("call_center.by"), id: "by" },
    { label: t("call_center.description"), id: "description" },
  ];

  const exportData = clients?.results?.map((client) => ({
    id: client.id,
    full_name: client.full_name,
    contact: client.contact,
    procedure_name: client?.display_procedure?.name,
    procedure_date: client?.procedure_date,
    status: client?.status_info?.name,
    price: client.price,
    by: `${client?.display_user?.first_name} ${client?.display_user?.last_name}`,
    description: client?.description || "",
  }));

  const getRowStyle = (client) => {
    if (client?.status_info?.id) {
      return {
        backgroundColor: statusColors[client.status_info.id] || "inherit",
      };
    }
    return {};
  };

  return (
    <Grid container justifyContent={"center"}>
      <Paper sx={{ padding: "15px", width: "100%", mt: 2 }}>
        <Grid item xs={12} md={11.5}>
          <Grid
            container
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Typography
              variant="h5"
              fontWeight={"bold"}
              align="center"
              sx={{ padding: 2 }}
            >
              {t("call_center.clientsList")}
            </Typography>
          </Grid>

          {/* Filters: Search, ID, Status, Follow-up by, Created At */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={2}
            gap={2}
            mt={2}
          >
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <TextField
                type="search"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiOutlinedInput-root": { borderRadius: "4px" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder={t("call_center.search")}
              />

              {/* Status filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={statusID}
                  onChange={(e) => setStatusID(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">{t("call_center.all")}</MenuItem>
                  {status?.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Follow-up By filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={followUpByID}
                  onChange={(e) => setFollowUpByID(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">{t("call_center.all")}</MenuItem>
                  {usersData?.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Created At filter */}
              <TextField
                type="date"
                size="small"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiOutlinedInput-root": { borderRadius: "4px" },
                }}
              />

              {/* Reset Filters */}
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setSearchTerm("");
                  setStatusID("");
                  setID("");
                  setFollowUpByID("");
                  setCreatedAt("");
                  setPage(1);
                }}
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "2px",
                  margin: "0px 3px",
                }}
              >
                {t("call_center.resetFilters")}
              </Button>
            </Box>
            <ExportToPDF
              data={exportData}
              columns={exportColumns}
              title={t("call_center.clientsList")}
            />
          </Box>

          <TableContainer sx={{ marginTop: 2, overflowY: "auto" }}>
            {isLoading ? (
              <Loading />
            ) : isError ? (
              <ErrorPage />
            ) : (
              <Table stickyHeader>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.id")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.name")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.contact")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.procedure")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.procedureDate")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.status")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.price")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.by")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.registrationDate", "Registration Date")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.description")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.action")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                  {clients?.results?.map((client, index) => (
                    <TableRow key={index} hover style={getRowStyle(client)}>
                      <TableCell style={tableCellStyle}>{client.id}</TableCell>
                      <TableCell style={tableCellStyle}>
                        {client.full_name}
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        {client.contact}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", textAlign: "center" }}
                      >
                        {client?.display_procedure?.name}
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        {FormatDateTime(client?.procedure_date)}
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        <Typography
                          fontWeight={"bold"}
                          border={"1px solid #d6d0d0"}
                          padding={"5px"}
                          borderRadius={"10px"}
                          textAlign={"center"}
                        >
                          {client?.status_info?.name}
                        </Typography>
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        {client.price}
                      </TableCell>
                      <TableCell>
                        {client?.display_user?.first_name}{" "}
                        {client?.display_user?.last_name}
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        {FormatDateTime(client?.created_at)}
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        {client?.description}
                      </TableCell>

                      <TableCell style={tableCellStyle}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          sx={{ textTransform: "capitalize" }}
                          onClick={() => {
                            setOpenEdit(true);
                            setEditData(client);
                          }}
                        >
                          {t("call_center.edit", "Edit")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Typography fontWeight={"bold"} sx={{ padding: "10px 0px" }}>
              Total: {total}
            </Typography>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={10}
            mb={5}
          >
            <Box display="flex" alignItems="center">
              <Typography>{t("call_center.rowsPerPage")}:</Typography>
              <FormControl
                sx={{
                  ml: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { border: "none" },
                  },
                }}
                size="small"
              >
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalClient / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
            />
          </Box>
        </Grid>

        {/* EditClient Dialog */}
        {openEdit && (
          <EditClient
            openEdit={openEdit}
            data={editData}
            onClose={() => {
              setOpenEdit(false);
              setEditData(null);
            }}
          />
        )}
      </Paper>
    </Grid>
  );
};

export default AllClients;
